import React from 'react';
import { Link } from 'react-router-dom';
import './Services.css';

type ServiceDef = {
  id: string;
  icon: JSX.Element;
  title: string;
  category: string;
  headline: string;
  body: string;
  features: string[];
  note?: string;
};

const services: ServiceDef[] = [
  {
    id: 'striping',
    icon: <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="9" x2="9" y2="21"/></svg>,
    title: 'Parking Lot Striping',
    category: 'Commercial',
    headline: 'Precision lines that organize, protect, and impress.',
    body: 'A well-striped parking lot isn\'t cosmetic — it manages traffic, prevents accidents, and signals professionalism to every customer and tenant. We deliver crisp, uniform stall striping for strip malls, office parks, retail centers, industrial properties, and multi-family common areas.',
    features: [
      'Full re-stripe and new lot layout',
      'Stall optimization for maximum capacity',
      'Fire lane markings and curb paint',
      'Loading zone and directional arrows',
      'Night and weekend scheduling available',
      'Before/after documentation provided',
    ],
    note: 'We price by lot size with no hidden mobilization surprises.',
  },
  {
    id: 'ada',
    icon: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    title: 'ADA / TAS Compliance',
    category: 'Federal Compliance',
    headline: 'Protect your business. Welcome everyone.',
    body: 'Non-compliant accessible parking isn\'t just an inconvenience — it\'s a federal liability. First-time ADA violations can run $75,000+. We build every compliance job around the 2010 ADA Standards and Texas Accessibility Standards (TAS): correct stall and aisle widths, van-accessible designation, ISA symbols, and sign mounting height verified to the inch.',
    features: [
      'ADA/TAS parking compliance audit',
      'Accessible and van-accessible stall striping',
      'Proper access aisle markings (ISA symbols)',
      'Sign installation — 60" minimum mounting height verified',
      '"Van Accessible" designation where required',
      'Closeout documentation packet (photos + measurements)',
    ],
    note: 'If your project has a construction cost of $50K+, Texas requires submission to a Registered Accessibility Specialist (RAS). We coordinate with your team.',
  },
  {
    id: 'pressure',
    icon: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>,
    title: 'Pressure Washing',
    category: 'Commercial & Residential',
    headline: 'Strip years of grime in hours.',
    body: 'Houston\'s climate is relentless — heat, humidity, pollen, and storms degrade commercial surfaces fast. Our commercial-grade pressure washing restores concrete, asphalt, brick, and pavers to like-new condition. For commercial properties, a clean lot and exterior is the first thing customers see.',
    features: [
      'Parking lots and driveways',
      'Building exteriors and storefronts',
      'Sidewalks, walkways, and entryways',
      'Dumpster pads and service areas',
      'Drive-throughs and loading docks',
      'Residential driveways, patios, and pool decks',
    ],
    note: 'We always clean before we stripe — it\'s built into our process at no extra charge on combo jobs.',
  },
  {
    id: 'softwash',
    icon: <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg>,
    title: 'Soft Wash',
    category: 'Residential',
    headline: 'Deep clean without the damage.',
    body: 'High pressure isn\'t right for every surface. Soft washing uses low pressure combined with professional-grade biodegradable solutions to eliminate algae, mold, mildew, and biological growth at the root — not just on the surface. Results last longer because the source is treated, not just blasted.',
    features: [
      'Painted stucco and EIFS systems',
      'Vinyl, wood, and fiber cement siding',
      'Roof surfaces and overhangs',
      'Window trim and screens',
      'Fencing and garden walls',
      'Long-lasting results — kills the growth source',
    ],
    note: 'Not sure if your surface needs pressure wash or soft wash? We assess on-site and recommend the right treatment.',
  },
  {
    id: 'junk',
    icon: <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>,
    title: 'Junk Removal',
    category: 'Commercial',
    headline: 'We haul it. You forget it ever existed.',
    body: 'Accumulated debris, bulk waste, old equipment, and unwanted material slow down your operations and reduce curb appeal. Our commercial junk removal service handles fast, professional hauling for office cleanouts, construction debris, retail overstock, and general property cleanup — with responsible disposal in full compliance with local ordinances.',
    features: [
      'Commercial property cleanouts',
      'Construction and renovation debris',
      'Bulk furniture and fixture removal',
      'Retail and warehouse overstock disposal',
      'Appliance and equipment removal',
      'Lawful disposal and recycling where applicable',
    ],
    note: 'Hazardous materials and regulated waste are outside scope — we\'ll tell you upfront if we see anything that needs special handling.',
  },
  {
    id: 'dumpster',
    icon: <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></svg>,
    title: 'Dumpster & Can Cleanup',
    category: 'Commercial',
    headline: 'The part of your property clients notice more than you think.',
    body: 'Dumpster areas are pest magnets, odor sources, and code violations waiting to happen. Regular high-pressure cleaning of dumpster pads, enclosures, and waste receptacles keeps your property compliant, pest-resistant, and presentable — for both customers and health inspectors.',
    features: [
      'Dumpster pad pressure washing',
      'Enclosure and surrounding area cleaning',
      'Grease and organic waste breakdown',
      'Odor treatment',
      'Can and receptacle exterior cleaning',
      'Recurring maintenance scheduling available',
    ],
    note: 'Many municipalities require dumpster pad maintenance. Ask us about setting up a recurring schedule.',
  },
];

const Services: React.FC = () => (
  <div className="services-page">
    <section className="page-hero section--dark">
      <div className="container-xl">
        <span className="section-label">What We Offer</span>
        <h1 className="section-title section-title--white">Commercial Services</h1>
        <p className="section-sub section-sub--white">
          Full-service commercial property maintenance — striping, compliance, washing, junk removal, and more.
          Built for commercial portfolios. Delivered with veteran precision.
        </p>
      </div>
    </section>
    <div className="red-bar" />

    <section className="section">
      <div className="container-xl">
        <div className="services-detail">
          {services.map((s, i) => (
            <div
              className={`svc-detail d-flex flex-column flex-md-row gap-4 gap-md-5 py-5 border-bottom${i % 2 === 1 ? ' bg-light' : ''}`}
              key={s.id}
              id={s.id}
            >
              <div className="svc-detail__icon-col d-flex flex-column align-items-center gap-2 flex-shrink-0">
                <div className="svc-detail__icon">{s.icon}</div>
                <span className="tag tag--navy">{s.category}</span>
              </div>
              <div className="svc-detail__content">
                <h2>{s.title}</h2>
                <p className="svc-detail__headline">{s.headline}</p>
                <p className="svc-detail__body">{s.body}</p>
                <ul className="row row-cols-1 row-cols-md-2 g-2 list-unstyled svc-detail__features">
                  {s.features.map((f, j) => (
                    <li className="col d-flex align-items-start gap-2" key={j}>
                      <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                      {f}
                    </li>
                  ))}
                </ul>
                {s.note && <p className="svc-detail__note">⚑ {s.note}</p>}
                <Link to="/contact" className="btn btn-brand-primary mt-4">
                  Get a Quote for This Service →
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="cta-banner-svc section--dark">
      <div className="container-xl">
        <h2>Not sure which service you need?</h2>
        <p>We do free on-site assessments. Tell us your property address and we'll walk you through exactly what it needs.</p>
        <div className="d-flex flex-wrap gap-3 mt-4">
          <Link to="/contact" className="btn btn-brand-primary">Schedule a Free Assessment</Link>
          <a href="tel:3464194499" className="btn btn-brand-outline-white">(346) 419-4499</a>
        </div>
      </div>
    </section>
  </div>
);

export default Services;
